import { AttendanceCheckDto } from '@diritto/common/activityPoint/dtos/attendance-check.dtos';
import { WsEpisodeDto } from '@diritto/common/episode/dtos/ws-episode.dto';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { range, removeNull } from '@diritto/common/utils/array';
import { WeekDays, WeekDayString } from '@diritto/common/utils/date';
import styled from '@emotion/styled';
import { GlossIcon } from 'icons/GlossIcon';
import { LeafIcon } from 'icons/LeafIcon';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';

const convertWeekdayToSundayStart = (weekday: number) => (weekday === 7 ? 0 : weekday);

export const Calendar = ({
  yearMonth,
  attendanceChecks,
  episodeIds,
  episodes,
}: {
  yearMonth: string;
  attendanceChecks?: AttendanceCheckDto[];
  episodeIds?: number[];
  episodes?: Record<number, WsEpisodeDto>;
}) => {
  const attends = useMemo(
    () => (attendanceChecks ? attendanceChecks.map(attend => attend.attendAt) : []),
    [attendanceChecks],
  );

  const episodeFirstOpeneds = useMemo(
    () =>
      removeNull(
        (episodes && episodeIds)?.map(episodeId => {
          const episode = episodes[episodeId];
          if (!episode || !episode.firstOpenedAt) {
            return null;
          }

          return DateTime.fromJSDate(new Date(episode.firstOpenedAt)).setZone('Asia/Seoul').toFormat('yyyy-MM-dd');
        }) ?? [],
      ),
    [episodeIds, episodes],
  );

  const calendar = useMemo(() => {
    const targetAt = DateTime.fromFormat(yearMonth, 'yyyy-MM');
    const monthDays = range(targetAt.daysInMonth).map(day => targetAt.set({ day: day + 1 }));
    const calendarItems: { index: number; days: { index: number; date: DateTime | undefined }[] }[] = [];
    let index = 0;
    let dayIndex = 0;
    let week = 0;

    while (dayIndex <= monthDays.length - 1) {
      calendarItems.push({ index: week, days: [] });

      for (let weekday = 0; weekday < 7; weekday += 1) {
        if (monthDays[dayIndex] && convertWeekdayToSundayStart(monthDays[dayIndex].weekday) === weekday) {
          calendarItems[week].days.push({ index, date: monthDays[dayIndex] });
          dayIndex += 1;
        } else {
          calendarItems[week].days.push({ index, date: undefined });
        }
        index += 1;
      }

      week += 1;
    }
    return calendarItems;
  }, [yearMonth]);

  return (
    <StyledCalendar>
      <StyledCalendarDayOfTheWeek>
        {WeekDays.map(it => (
          <div key={it}>{WeekDayString[it]}</div>
        ))}
      </StyledCalendarDayOfTheWeek>
      <StyledCalendarWeeks>
        {calendar.map(week => (
          <StyledCalendarWeek key={`${yearMonth}-${week.index}`}>
            {week.days.map(info => (
              <div key={info.index}>
                {!info.date && <StyledCalendarDaySpace key={info.index} />}
                {info.date && attends?.includes(info.date.toFormat('yyyy-MM-dd')) && (
                  <StyledCalendarDayAttendToday key={info.index}>
                    {episodeFirstOpeneds.includes(info.date.toFormat('yyyy-MM-dd')) && (
                      <StyledEpisodeOpenToday>
                        <LeafIcon className="w-[8px] h-[8px]" />
                      </StyledEpisodeOpenToday>
                    )}
                    <StyledGlossIcon>
                      <GlossIcon />
                    </StyledGlossIcon>
                    {info.date.day}
                  </StyledCalendarDayAttendToday>
                )}
                {info.date && !attends?.includes(info.date.toFormat('yyyy-MM-dd')) && (
                  <StyledCalendarDayNotYet key={info.index}>{info.date.day}</StyledCalendarDayNotYet>
                )}
              </div>
            ))}
          </StyledCalendarWeek>
        ))}
      </StyledCalendarWeeks>
    </StyledCalendar>
  );
};

const StyledCalendar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 32px;
  margin-bottom: 24px;
`;

const StyledCalendarWeeks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledCalendarDayOfTheWeek = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: 8px;

  color: ${Color.Black};
  font-weight: ${FontWeight.Bold};
  ${Typography.XXSmall};

  border-bottom: 1px solid ${Color.Gray200};

  & > div {
    display: flex;
    flex-grow: 0;
    align-items: center;
    justify-content: space-around;
    width: 32px;
  }
`;

const StyledCalendarWeek = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-flow: row wrap;
`;

const StyledCalendarDaySpace = styled.div`
  width: 32px;
  height: 32px;
`;

const StyledCalendarDayNotYet = styled.div`
  position: relative;

  border: 1px dashed ${Color.Primary400};
  border-radius: 40px;
  width: 32px;
  height: 32px;

  display: flex;
  justify-content: center;
  align-items: center;

  color: ${Color.Gray700};
  font-weight: ${FontWeight.Medium};
  ${Typography.XXSmall};
`;

const StyledCalendarDayAttendToday = styled.div`
  position: relative;
  background: ${Color.Primary500};
  border-radius: 40px;
  width: 32px;
  height: 32px;

  display: flex;
  justify-content: center;
  align-items: center;

  color: ${Color.White};
  font-weight: ${FontWeight.Bold};
  ${Typography.XXSmall};
`;

const StyledEpisodeOpenToday = styled.div`
  position: absolute;
  left: 50%;
  right: 25%;
  top: calc(-50% + 1px);
  bottom: 100%;
`;

const StyledGlossIcon = styled.div`
  position: absolute;
  left: 20px;
  top: 13px;
`;
